import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Clock, ChevronDown } from "lucide-react";

function formatStartTime(timeStr) {
  const [hStr, mStr] = timeStr.split(":");
  let h = Number(hStr);
  const ampm = h >= 12 ? "PM" : "AM";
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

// The backend computes slots in IST (`time`/`label`) -- that's what's
// actually submitted as preferred_time and what the real Google Calendar
// event gets created in, so it stays the source of truth. `start_utc` is
// the same instant in absolute UTC, added only so this can show each
// visitor their own local wall-clock time instead of always IST. For an
// IST visitor this naturally comes out identical to the old IST-only
// display, so there's no separate branch needed for that case.
function formatLocalStart(slot) {
  if (!slot?.start_utc) return null;
  const d = new Date(slot.start_utc);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return null;
  }
}

function displayTime(slot) {
  return formatLocalStart(slot) || formatStartTime(slot.time);
}

/**
 * Replaces the plain native <select> list of real open slots (from
 * GET /api/public/booking/availability) with a dropdown panel of rounded
 * chip buttons, one per slot, showing only its start time -- in the
 * visitor's own local timezone, derived from `start_utc` (the backend's
 * `label` field -- "9:00 AM - 9:30 AM IST" -- is still sent and still used
 * nowhere else; this component formats its own display string). Selecting a chip calls
 * onChange with that exact same "HH:MM" (IST) value the native <select>
 * would have -- BookingModal's state/payload are untouched. UI only. The
 * manual <input type="time"> fallback (when a persona's calendar isn't
 * configured) is a separate, native element this
 * component doesn't touch.
 */
export default function TimeSlotPicker({ id, testId, value, onChange, slots, loading, hasError, disabled: disabledProp }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const selectedSlot = useMemo(() => slots.find((s) => s.time === value), [slots, value]);
  const disabled = disabledProp || loading || slots.length === 0;

  const updateRect = () => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close if the slot list changes out from under an open panel (e.g. the
  // date was changed while this was open) rather than show stale chips.
  useEffect(() => {
    setOpen(false);
  }, [slots]);

  const pick = (timeStr) => {
    onChange(timeStr);
    setOpen(false);
  };

  // Matches the trigger's own width exactly (label-start to label-end),
  // rather than a fixed panel width that could run wider or narrower than
  // the field it belongs to.
  const panelStyle = useMemo(() => {
    if (!rect) return { display: "none" };
    return { position: "fixed", top: rect.bottom + 6, left: rect.left, width: rect.width };
  }, [rect]);

  const triggerLabel = loading
    ? "Loading times…"
    : slots.length === 0
      ? "No times available this day"
      : selectedSlot
        ? displayTime(selectedSlot)
        : "Select a time";

  const fieldBase =
    "w-full flex items-center gap-2 rounded-md px-3.5 py-2.5 text-sm bg-white border text-left focus:outline-none focus:ring-2 focus:ring-royal/30 focus:border-royal";

  return (
    <div>
      <button
        type="button"
        id={id}
        data-testid={testId}
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={Boolean(hasError)}
        className={`${fieldBase} ${hasError ? "border-red-400" : "border-line"} ${disabled ? "opacity-70 cursor-not-allowed" : ""} ${selectedSlot ? "text-ink" : "text-ink-soft/70"}`}
      >
        <Clock aria-hidden size={16} className="text-royal-deep flex-shrink-0" />
        <span className="flex-1 truncate">{triggerLabel}</span>
        <ChevronDown aria-hidden size={14} className="text-ink-soft flex-shrink-0" />
      </button>

      {open &&
        createPortal(
          <div ref={panelRef} style={panelStyle} className="zt-slot-popover" role="listbox" aria-label="Preferred time">
            <div className="zt-slot-grid">
              {slots.map((s) => (
                <button
                  type="button"
                  key={s.time}
                  role="option"
                  aria-selected={s.time === value}
                  onClick={() => pick(s.time)}
                  className={`zt-slot-chip ${s.time === value ? "is-selected" : ""}`}
                >
                  {displayTime(s)}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
